// src/onboarding/activation/ActivationIntroSequence.jsx
/**
 * ActivationIntroSequence.jsx
 * -----------------------------------------------------------------------------
 * PURPOSE
 * Character-led 4-screen onboarding with:
 *  - Pulsing progress bar (top)
 *  - Toro (or image) + speech bubble that types microcopy (middle)
 *  - Skip (disabled) + Continue (bottom)
 *  - ✨ Sparkle effect when typing finishes
 *  - 🔊 Subtle sound ping after typing finishes (only if audio is unlocked)
 *
 * AUTOPLAY POLICY
 * - We only play sounds AFTER a user gesture has called ensureUserAudioOnce().
 * - This component auto-unlocks audio on the first "Continue" click, then
 *   subsequent screens will ping when typing completes.
 *
 * ROUTE
 * - /onboarding/intro → ActivationIntroSequence
 *
 * BRAND
 * - Orange:   #FF7C24
 * - Charcoal: #232830
 *
 * © FinTorro — Always include in-code documentation in generated files.
 */
/**
 * ActivationIntroSequence.jsx
 * -----------------------------------------------------------------------------
 * FinTorro activation intro sequence (PropFunding version)
 * Matches TemplateOnboardingAndLessonScreen styling.
 * Fixes progress bar + adds dynamic course-based content.
 * © FinTorro
 */
/**
 * ActivationIntroSequence.jsx
 * ---------------------------------------------------------------------------
 * FinTorro PropFunding Onboarding Flow
 * Layout: Title → Lottie (below title) → Short Description
 * Buttons: (No / Yes) → (No / Yes) → (Continue)
 * Progress Bar: Green fill, numbered step counter
 * ---------------------------------------------------------------------------
 *//**
 * ActivationIntroSequence.jsx — PropFunding Edition (Refined Layout)
 * ---------------------------------------------------------------------------
 * Layout:
 *  Title → Coin Sparkle Animation → Description
 *  Divider Line → No (Left) / Yes (Right)
 * ---------------------------------------------------------------------------
 */

/**
 * ActivationIntroSequence.jsx — PropFunding Edition (Fixed Layout)
 * ---------------------------------------------------------------------------
 * - No vertical scroll (100vh locked)
 * - Buttons centered like Skip/Continue
 * - Coin Sparkle below title
 * ---------------------------------------------------------------------------
 */

/**
 * ActivationIntroSequence.jsx — PropFunding Edition (Fixed Layout)
 * ---------------------------------------------------------------------------
 * - No vertical scroll (100vh locked)
 * - Buttons centered like Skip/Continue
 * - Coin Sparkle below title
 * ---------------------------------------------------------------------------
 */

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import LottieSmart from "../../components/animation/LottieSmart.jsx";

import meditatingBull from "../../assets/animations/meditating_bull.json";
import coinSparkle from "../../assets/animations/coin_sparkle.json";
import rocketBull from "../../assets/animations/coin_sparkle.json"; // later
import fintorroJumpingBull from "../../assets/animations/fintorro-lottie-jumping-bull.json";

const ANIMATIONS = [meditatingBull, fintorroJumpingBull, rocketBull];



const COLORS = {
  bg: "#0B0D11",
  orange: "#FF7C24",
  green: "#00D26A",
  white: "#FFFFFF",
  dim: "#DDE2EA",
  border: "rgba(255,255,255,0.1)",
};

// -----------------------------------------------------------------------------
// SCREENS — PropFunding Content
// -----------------------------------------------------------------------------
const SCREENS = [
  {
    title: "Do you want to trade without risking your own funds?",
    desc: "Over 500,000 traders are earning funded accounts by passing prop-firm challenges. We’ll help you do the same — step by step.",
  },
  {
    title: "How does it work?",
    desc: "We’ll guide you through the entire prop funding process — from understanding firm rules to mastering evaluation strategies that help you pass confidently.",
  },
  {
    title: "Why take this PropFunding course?",
    desc: "Learn proven setups from funded traders, avoid costly mistakes, and unlock discounts on real prop firm challenges.",
  },
];

// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------
const styles = {
  page: {
    backgroundColor: COLORS.bg,
    color: COLORS.white,
    fontFamily: "Nunito Sans, sans-serif",
    height: "100vh",
    width: "100vw",
    overflow: "hidden", // no scroll
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
  },
  progressWrapper: {
    position: "relative",
    width: "90%",
    height: "8px",
    background: "rgba(255,255,255,0.08)",
    borderRadius: "999px",
    marginTop: "20px",
  },
  progressBar: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: `linear-gradient(90deg, ${COLORS.green}, #A8FF85)`,
    borderRadius: "999px",
    transition: "width 0.4s ease",
  }),
  progressText: {
    position: "absolute",
    right: "-55px",
    top: "-8px",
    fontSize: "13px",
    color: COLORS.dim,
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },
  title: {
    fontSize: "24px",
    fontWeight: 800,
    marginBottom: "16px",
  },
  lottieWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
  },
  desc: {
    fontSize: "16px",
    lineHeight: 1.6,
    color: COLORS.dim,
    maxWidth: "500px",
    margin: "0 auto",
  },
  divider: {
    width: "100%",
    height: "2px",
    backgroundColor: COLORS.orange,
    opacity: 0.9,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    paddingBottom: "28px",
  },
  btnRow: {
    display: "flex",
    justifyContent: "center",
    gap: "24px",
    marginTop: "20px",
  },
  btnBase: {
    padding: "12px 28px",
    borderRadius: "14px",
    fontWeight: 700,
    fontSize: "16px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  btnNo: {
    background: "rgba(255,255,255,0.08)",
    color: COLORS.dim,
    border: `1px solid ${COLORS.border}`,
  },
  btnYes: {
    background: COLORS.orange,
    color: COLORS.white,
    border: "none",
    boxShadow: "0 6px 14px rgba(255,124,36,0.25)",
  },
  btnContinue: {
    background: COLORS.orange,
    color: COLORS.white,
    border: "none",
    borderRadius: "14px",
    fontWeight: 700,
    fontSize: "16px",
    padding: "12px 36px",
    marginTop: "20px",
    boxShadow: "0 6px 14px rgba(255,124,36,0.25)",
    cursor: "pointer",
  },
};

// -----------------------------------------------------------------------------
// COMPONENT
// -----------------------------------------------------------------------------
export default function ActivationIntroSequence() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const screen = SCREENS[idx];
  const pct = useMemo(() => ((idx + 1) / SCREENS.length) * 100, [idx]);

  const goNext = () => {
    if (idx < SCREENS.length - 1) setIdx((i) => i + 1);
    else navigate("/login?course=propfunding");
  };

  const goPrev = () => {
    if (idx > 0) setIdx((i) => i - 1);
  };

  return (
    <div style={styles.page}>
      {/* Progress Bar */}
      <div style={{ width: "80%", position: "relative", marginTop: "20px" }}>
        <div style={styles.progressWrapper}>
          <div style={styles.progressBar(pct)} />
        </div>
        <span style={styles.progressText}>
          {idx + 1} of {SCREENS.length}
        </span>
      </div>

      {/* Content */}
      <div style={styles.content}>
        <h1 style={styles.title}>{screen.title}</h1>

        {/* Coin Sparkle Animation */}
        <div style={styles.lottieWrap}>
          {/* <LottieSmart
            src="/assets/animations/meditating_bull.json"
            fallbackSrc="https://lottie.host/91cf2c8a-e958-4249-b2b8-9c5bc265516f/zmGw6EuVBZ.lottie"
            style={{ width: 160, height: 160 }}
            loop
            autoplay
          /> */}

<LottieSmart
  src={ANIMATIONS[idx]}
  loop
  autoplay
  style={{ width: 160, height: 160 }}
/>
        </div>

        <p style={styles.desc}>{screen.desc}</p>
      </div>

      {/* Divider */}
      <div style={styles.divider}></div>

      {/* Footer */}
      <footer style={styles.footer}>
        {idx < SCREENS.length - 1 ? (
          <div style={styles.btnRow}>
            <button style={{ ...styles.btnBase, ...styles.btnNo }} onClick={goPrev}>
              No
            </button>
            <button style={{ ...styles.btnBase, ...styles.btnYes }} onClick={goNext}>
              Yes
            </button>
          </div>
        ) : (
          <button style={styles.btnContinue} onClick={goNext}>
            Continue
          </button>
        )}
      </footer>
    </div>
  );
}




// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   ensureUserAudioOnce,
//   playHintPing, // soft, friendly ping
//   speak,        // optional TTS if you want Toro to "talk" later
// } from "./kit/soundFX.js"; // ← adjust path if your kit lives elsewhere

// import LottieSmart from "../../components/animation/LottieSmart.jsx";






// // -----------------------------------------------------------------------------
// // TUNABLES
// // -----------------------------------------------------------------------------
// const FIRST_MISSION_SLUG = "find-the-floor"; // change to your actual first lesson slug

// // In ActivationIntroSequence.jsx (near SCREENS)

// const ANIMS = {
//   toroWave: {
//     src: "/assets/animations/toro_wave.json",
//     fallbackSrc: "",           // optional backup URL
//     w: 140, h: 140, loop: true, autoplay: true,
//   },
//   xpBarFill: {
//     src: "/assets/animations/xp_bar_fill.json",
//     fallbackSrc: "",
//     w: 160, h: 160, loop: true, autoplay: true,
//   },
//   coinSparkle: {
//     src: "/assets/animations/coin_sparkle.json",
//     fallbackSrc: "https://lottie.host/17f89781-7c01-4038-b8eb-34f05839ad8e/I3He0k0iNH.json",
//     w: 160, h: 160, loop: true, autoplay: true,
//   },
//   flagPlant: {
//     src: "/assets/animations/flag_plant.json",
//     fallbackSrc: "",
//     w: 160, h: 160, loop: false, autoplay: true,
//   },
// };

// // Swap emoji with your assets anytime (set imgSrc to PNG/SVG)
// // const SCREENS = [
// //   { id: "meet-toro",      title: "Meet Toro",              body: "Your guide on this trading adventure.", emoji: "🐂", imgSrc: "" },
// //   { id: "what-is-xp",     title: "XP = Skill",             body: "Every correct move grows your level.",   emoji: "📈", imgSrc: "" },
// //   { id: "what-are-tokens",title: "Tokens = Currency",      body: "Spend, unlock, or gift to friends.",     emoji: "🪙", imgSrc: "" },
// //   { id: "first-mission",  title: "Mission: Find the Floor",body: "Mark support to earn XP + Tokens.",      emoji: "🧱", imgSrc: "" },
// // ];

// // ✅ attach `anim` to each screen


// const SCREENS = [
//   { id: "meet-toro",       title: "Meet Toro",               body: "Your guide on this trading adventure.", anim: ANIMS.toroWave },
//   { id: "what-is-xp",      title: "XP = Skill",              body: "Every correct move grows your level.",   anim: ANIMS.xpBarFill },
//   { id: "what-are-tokens", title: "Tokens = Currency",       body: "Spend, unlock, or gift to friends.",     anim: ANIMS.coinSparkle },
//   { id: "first-mission",   title: "Mission: Find the Floor", body: "Mark support to earn XP + Tokens.",      anim: ANIMS.flagPlant },
// ];






// // -----------------------------------------------------------------------------
// // LOCAL STYLES (inline for portability — move to CSS/Tailwind when you prefer)
// // -----------------------------------------------------------------------------
// const styles = {
//   shell: {
//     maxWidth: 920, margin: "24px auto", padding: "20px 20px 28px",
//     background: "rgba(255,255,255,0.03)", borderRadius: 16,
//     border: "1px solid rgba(255,255,255,0.08)",
//   },
//   header: { padding: "4px 6px 16px" },
//   title: { fontSize: 22, fontWeight: 800, color: "#FFFFFF" },
//   stage: {
//     position: "relative", display: "grid", placeItems: "center",
//     minHeight: 260, borderRadius: 16, background: "rgba(255,255,255,0.02)",
//     border: "1px dashed rgba(255,255,255,0.08)", overflow: "hidden",
//   },
//   footer: {
//     display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 18,
//   },
//   skip: {
//     background: "rgba(255,255,255,0.06)", color: "#AAB0B7",
//     border: "1px solid rgba(255,255,255,0.12)", padding: "10px 16px",
//     borderRadius: 12, cursor: "not-allowed", opacity: 0.6,
//   },
//   continue: {
//     background: "#FF7C24", color: "#FFFFFF", padding: "12px 20px",
//     borderRadius: 14, border: "none", fontWeight: 700,
//     boxShadow: "0 8px 16px rgba(255,124,36,0.25)",
//   },
//   emoji: { fontSize: 64, lineHeight: 1 },
//   img: { width: 110, height: 110, objectFit: "contain" },
// };

// const AnimCSS = () => (
//   <style>{`
//     /* progress bar pulse */
//     @keyframes ftPulse {
//       0% { transform: scaleX(0.06); opacity: .7; }
//       45% { transform: scaleX(1); opacity: 1; }
//       100% { transform: scaleX(.98); opacity: .9; }
//     }
//     .ft-progress-wrap {
//       position: relative; height: 8px; width: 100%;
//       background: rgba(255,255,255,0.08);
//       border-radius: 999px; overflow: hidden;
//       box-shadow: inset 0 0 0 1px rgba(255,255,255,0.06);
//     }
//     .ft-progress-bar {
//       height: 100%; transform-origin: left center;
//       background: linear-gradient(90deg, #FF7C24, #FFB800);
//       border-radius: 999px;
//       animation: ftPulse 1400ms ease-in-out infinite;
//     }

//     /* character idle float */
//     @keyframes ftFloat {
//       0% { transform: translateY(0px); }
//       50% { transform: translateY(-8px); }
//       100% { transform: translateY(0px); }
//     }
//     .ft-float {
//       animation: ftFloat 2200ms ease-in-out infinite;
//       will-change: transform;
//     }

//     /* bubble pop-in */
//     @keyframes ftPopIn {
//       0% { transform: scale(.92) translateY(6px); opacity: 0; }
//       100% { transform: scale(1) translateY(0); opacity: 1; }
//     }
//     .ft-pop {
//       animation: ftPopIn 240ms cubic-bezier(.2,.7,.2,1) both;
//     }

//     /* sparkle burst (discrete stars) */
//     @keyframes ftSparkle {
//       0%   { transform: translateY(0) scale(.6); opacity: 0; }
//       20%  { opacity: 1; }
//       100% { transform: translateY(-10px) scale(1); opacity: 0; }
//     }
//     .ft-sparkles {
//       position: absolute; top: 6px; right: 10px; pointer-events: none;
//       display: flex; gap: 6px;
//     }
//     .ft-sparkles > span {
//       display: inline-block; font-size: 14px; line-height: 1;
//       animation: ftSparkle 700ms ease-out forwards;
//     }
//     .ft-sparkles > span:nth-child(2) { animation-delay: 60ms; }
//     .ft-sparkles > span:nth-child(3) { animation-delay: 120ms; }
//   `}</style>
// );

// /**
//  * SpeechBubble
//  * - Pops in and types text.
//  * - When typing completes, triggers onDone() (to show sparkles, play ping, etc.)
//  */
// function SpeechBubble({ text, typingMs = 25, maxWidth = 560, onStart,  onDone }) {
//   const [shown, setShown] = useState("");
//   const [done, setDone] = useState(false);
//   const timerRef = useRef(null);

//   useEffect(() => {
//     setShown("");
//     setDone(false);
//     clearInterval(timerRef.current);

//     // NEW: notify parent that typing is starting now
//     onStart?.();  // <-- this fires immediately when typing begins

//     let i = 0;
//     timerRef.current = setInterval(() => {
//       i++;
//       setShown(text.slice(0, i));
//       if (i >= text.length) {
//         clearInterval(timerRef.current);
//         setDone(true);
//         onDone?.();
//       }
//     }, typingMs);
//     return () => clearInterval(timerRef.current);
//   }, [text, typingMs, onStart, onDone]);



//   return (
//     <div
//       className="ft-pop"
//       style={{
//         position: "absolute",
//         top: 12,
//         left: "50%",
//         transform: "translateX(-50%)",
//         maxWidth,
//         padding: "12px 16px",
//         background: "rgba(255,124,36,0.10)", // soft orange wash
//         color: "#232830",
//         borderRadius: 14,
//         border: "1px solid rgba(255,124,36,0.35)",
//         backdropFilter: "blur(2px)",
//         boxShadow: "0 6px 22px rgba(0,0,0,0.16)",
//       }}
//     >
//       {/* sparkles (shown when typing finishes) */}
//       {done && (
//         <div className="ft-sparkles" aria-hidden="true">
//           <span>✨</span><span>✦</span><span>✨</span>
//         </div>
//       )}

//       <div
//         style={{
//           fontSize: 16,
//           lineHeight: 1.35,
//           fontWeight: 600,
//           letterSpacing: 0.2,
//         }}
//       >
//         {shown}
//       </div>

//       {/* tail (double layer for border illusion) */}
//       <div
//         aria-hidden="true"
//         style={{
//           position: "absolute",
//           bottom: -10,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 0, height: 0,
//           borderLeft: "10px solid transparent",
//           borderRight: "10px solid transparent",
//           borderTop: "10px solid rgba(255,124,36,0.35)",
//         }}
//       />
//       <div
//         aria-hidden="true"
//         style={{
//           position: "absolute",
//           bottom: -9,
//           left: "50%",
//           transform: "translateX(-50%)",
//           width: 0, height: 0,
//           borderLeft: "9px solid transparent",
//           borderRight: "9px solid transparent",
//           borderTop: "9px solid rgba(255,124,36,0.10)",
//         }}
//       />
//     </div>
//   );
// }

// export default function ActivationIntroSequence() {
//   const navigate = useNavigate();
//   const [idx, setIdx] = useState(0);
//   const [audioUnlocked, setAudioUnlocked] = useState(false);
// const [typedDone, setTypedDone] = useState(false); // NEW: track typing completion

//   const pct = useMemo(() => ((idx + 1) / SCREENS.length) * 100, [idx]);
//   const screen = SCREENS[idx];

  
// const pendingSpeakRef = useRef(null);

// function startSpeechIfPossible(text) {
//   if (audioUnlocked) {
//     try { speak(text); } catch {}
//     pendingSpeakRef.current = null;
//   } else {
//     // queue it; will fire the instant audio unlocks
//     pendingSpeakRef.current = text;
//   }
// }

// useEffect(() => {
//   const unlock = () => {
//     if (!audioUnlocked) {
//       try { ensureUserAudioOnce(); } catch {}
//       setAudioUnlocked(true);
//     }
//   };
//   window.addEventListener("pointerdown", unlock, { once: true, passive: true });
//   window.addEventListener("keydown", unlock, { once: true, passive: true });
//   window.addEventListener("touchstart", unlock, { once: true, passive: true });
//   return () => {
//     window.removeEventListener("pointerdown", unlock);
//     window.removeEventListener("keydown", unlock);
//     window.removeEventListener("touchstart", unlock);
//   };
// }, [audioUnlocked]);

// useEffect(() => {
//   if (audioUnlocked && pendingSpeakRef.current) {
//     try { speak(pendingSpeakRef.current); } catch {}
//     pendingSpeakRef.current = null;
//   }
// }, [audioUnlocked]);






  

//   // Called when the bubble finishes typing on each screen
//   const handleTypedDone = () => {
//     // Play a subtle ping ONLY if audio has been unlocked by user gesture
//     if (audioUnlocked) {
//       try { playHintPing(); } catch {}
//       // Optional TTS (keep super short):
//       try { speak(screen.body); } catch {}
//     }
//   };

//   const goNext = () => {
//     if (!audioUnlocked) {
//       try { ensureUserAudioOnce(); } catch {}
//       setAudioUnlocked(true);
//     }
//     if (idx < SCREENS.length - 1) {
//       setTypedDone(false);       // reset for next screen
//       setIdx((i) => i + 1);
//     } else {
//       navigate(`/onboarding/lesson?slug=${FIRST_MISSION_SLUG}`);
//     }
//   };

//   const handleFirstUserGesture = () => {
//     if (!audioUnlocked) {
//       try { ensureUserAudioOnce(); } catch {}
//       setAudioUnlocked(true);
//       // If the text already finished typing on the first screen, speak now
//       if (typedDone) {
//         try { speak(screen.body); } catch {}
//       }
//     }
//   };
  
  

//   return (


//     <div style={styles.shell} role="region" aria-label="Onboarding Intro" onPointerDown={handleFirstUserGesture} >
//       <AnimCSS />

//       {/* Top: pulsing progress */}
//       <div className="ft-progress-wrap" aria-label="Progress">
//         <div
//           className="ft-progress-bar"
//           style={{ width: `${pct}%` }}
//           role="progressbar"
//           aria-valuemin={0}
//           aria-valuemax={100}
//           aria-valuenow={Math.round(pct)}
//         />
//       </div>

//       {/* Minimal title (main copy is in the bubble) */}
//       <header style={styles.header}>
//         <h1 style={styles.title}>{screen.title}</h1>
//       </header>

//       {/* Stage: character + speech bubble */}
//       <main style={styles.stage}>
//         {/* <SpeechBubble text={screen.body} onDone={handleTypedDone} /> */}


//         {/* <SpeechBubble
//   text={screen.body}
//   onDone={() => {
//     setTypedDone(true);    // mark current line done
//     if (audioUnlocked) {
//       try { playHintPing(); } catch {}
//       try { speak(screen.body); } catch {}   // will speak if already unlocked
//     }
//   }}
// /> */}

// <SpeechBubble
//   text={screen.body}
//   onStart={() => {
//     // try to speak right as typing begins; if blocked, it's queued
//     startSpeechIfPossible(screen.body);
//   }}
//   onDone={() => {
//     setTypedDone(true);
//     // (optional) ping on finish if audio already unlocked
//     if (audioUnlocked) { try { playHintPing(); } catch {} }
//   }}
// />



//         {/* Character / Icon */}
//         {/* <div className="ft-float" aria-label="Character">
//           {screen.imgSrc ? (
//             <img alt="" src={screen.imgSrc} style={styles.img} />
//           ) : (
//             <div style={styles.emoji}>{screen.emoji}</div>
//           )}
//         </div> */}


// {/* Inside <main style={styles.stage}> … where the emoji/image was Lottie files */}

// {/* // Inside your stage/content: */}
// {/* <LottieSmart
//   src="/assets/animations/coin_sparkle.json"                // self-hosted primary
//   fallbackSrc="https://lottie.host/17f89781-7c01-4038-b8eb-34f05839ad8e/I3He0k0iNH.json" // optional
//   style={{ width: 160, height: 160 }}
// /> */}

// {/* // Inside <main style={styles.stage}> … where the emoji/Lottie was  - OL*/}


// <LottieSmart
//   src={screen.anim.src}
//   fallbackSrc={screen.anim.fallbackSrc}
//   loop={screen.anim.loop}
//   autoplay={screen.anim.autoplay}
//   style={{ width: screen.anim.w, height: screen.anim.h }}
// />

















// {/* 🔊 Sound unlock hint — disappears once audioUnlocked === true */}
// {!audioUnlocked && (
//     <div
//       style={{
//         position: "absolute",
//         bottom: 12,
//         left: "50%",
//         transform: "translateX(-50%)",
//         fontSize: 12,
//         color: "rgba(255,255,255,0.7)",
//         padding: "6px 10px",
//         borderRadius: 10,
//         background: "rgba(255,255,255,0.08)",
//         border: "1px solid rgba(255,255,255,0.12)",
//       }}
//     >
//       🔊 Tap to enable sound
//     </div>
//   )}





//       </main>

//       {/* Bottom actions */}
//       <footer style={styles.footer}>
//         <button type="button" style={styles.skip} disabled aria-disabled="true">
//           Skip
//         </button>
//         <button type="button" style={styles.continue} onClick={goNext}>
//           Continue
//         </button>
//       </footer>
//     </div>
//   );
// }

/**
 * DEV CHECKLIST
 * 1) Router:
 *    import ActivationIntroSequence from "./onboarding/activation/ActivationIntroSequence";
 *    <Route path="/onboarding/intro" element={<ActivationIntroSequence />} />
 *
 * 2) Sound:
 *    - First "Continue" click calls ensureUserAudioOnce() and flips audioUnlocked=true.
 *    - After that, each screen's typing-complete will play playHintPing().
 *
 * 3) Assets:
 *    - Swap emoji with Toro PNG/SVG by setting imgSrc in SCREENS.
 *
 * 4) Mission:
 *    - Update FIRST_MISSION_SLUG to match your first lesson in scenes.jsx.
 *
 * 5) Theming:
 *    - Adjust container/background colors if your shell differs (light/dark).
 */
